import {get, isEmpty} from "lodash"
import moment from "moment"
import React, {PureComponent} from "react"
import {Col, Row} from "react-bootstrap"

import withBranding, {WithBranding} from "@irati/brand/withBranding"
import redemptionService from "@irati/checkout/services/redemptions"
import {determineDealThumbnailImage} from "@irati/deals/helpers/deals/appearance"
import isAppOnly from "@irati/deals/helpers/deals/isAppOnly"
import {CheckoutRedemption, DealOrderProduct, DealType} from "@irati/types"
import {Body, H5, Label} from "@irati/ui/src/Typography"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import notify from "@irati/utils/notify/index"

import {hasRedemptionExpiredForCheckout as hasRedemptionExpiredHelper} from "@irati/deals/helpers/deals/isTimeLimited"
import {GradientOverlay} from "@irati/ui/src/GradientOverlay/GradientOverlay"
import {shouldDisplayDealAvatar} from "../../../utils/deals/imageUtils"
import ViewPerkOnAppOnly from "../../MyPerks/ViewPerkOnAppOnly"
import {
  DealAvatar,
  ItemContainer,
  LogoWrapper,
  MobileQty,
  StyledButton, StyledDeleteButtonCol, StyledDeleteHeaderCol, StyledHr,
  StyledRow
} from "../styled/MyPerksItem"
import TimeLimitedDeal from "./TimeLimitedDeal"

interface Props extends InjectIntl, WithBranding {
  redemption: CheckoutRedemption,
  onViewRedemption: (redemptionUuid: string) => void,
  onMarkAsUsed: (redemptionUuid: string) => void,
  usedPerksOptionActive: boolean,
  onArchiveSuccess?: (redemption: CheckoutRedemption) => void,
  onUnmarkRedemption?: () => void,
  withHr?: boolean
}

class MyPerkItem extends PureComponent<Props> {
  isMerchDealType = false
  thumbnailImage: string | null = null

  handleArchiveRedemption = () => {
    const {redemption, onArchiveSuccess, formatMessage} = this.props
    const {uuid, Etag} = redemption

    return redemptionService
      .archive(uuid, Etag)
      .then(() => {
        if (onArchiveSuccess) onArchiveSuccess(redemption)
      })
      .catch((error: PBError) => {
        notify.error({
          message: formatMessage({id: error.message}),
          position: "top-right"
        })
      })
  }

  getThumbnailImg() {
    if(this.thumbnailImage) return this.thumbnailImage

    const {redemption, branding} = this.props
    const deal = redemption.dealSnapshot!
    const isMerchType = redemption.dealSnapshot!.type === DealType.Merchandise

    if(isMerchType || shouldDisplayDealAvatar(deal)) {
      return determineDealThumbnailImage(deal, branding)
    }

    return null
  }

  render() {
    const {redemption, onViewRedemption, onMarkAsUsed, usedPerksOptionActive, formatMessage, withHr = true} = this.props
    const orderProducts = get(redemption, "order.products", [])
    const quantity = orderProducts?.reduce((acc: number, product: DealOrderProduct) => acc + product.items.length, 0)
    const perkIsAppOnly = isAppOnly(get(redemption, "dealSnapshot"))
    const perkIsUnavailable = get(redemption, "dealUnavailable")
    const hasRedemptionExpired = hasRedemptionExpiredHelper(redemption)
    const isMerchDealType = redemption?.dealSnapshot?.type === DealType.Merchandise

    this.thumbnailImage = this.getThumbnailImg()

    let ViewPerkComponent = (
      <StyledButton
        id="ViewPerkButtonId"
        primary
        full
        onClick={() => onViewRedemption(redemption.uuid)}
        small
        disabled={hasRedemptionExpired}>
        <FormattedMessage id="perks.components.myPerks.myPerksItem.view" />
      </StyledButton>
    )

    if (perkIsAppOnly) {
      ViewPerkComponent = <ViewPerkOnAppOnly />
    }

    return (
      <>
        {withHr && <StyledHr />}
        <ItemContainer addGreyBackground={perkIsUnavailable}>
          <StyledRow key={redemption.uuid}>
            <Col sm={2}>
              {this.thumbnailImage &&
              <LogoWrapper>
                <DealAvatar
                  src={this.thumbnailImage}
                  alt={get(redemption, "dealSnapshot.header", "placeholder image")}
                  extraSmall
                  withShadow />
                {isMerchDealType && <GradientOverlay gradientType="linear" />}
              </LogoWrapper>
              }
            </Col>
            <Col xs={4} sm={7}>
              <Row>
                <Col xs={12} sm={6} md={7}>
                  <H5 overflowWrap="break-word">
                    {get(redemption, "dealSnapshot.header", formatMessage({id: "perks.myPerks.dealHeader.placeholder"}))}
                  </H5>
                  {quantity > 0 && (
                    <MobileQty small>
                      <FormattedMessage id="perks.components.myPerks.myPerksItem.quantity" values={{quantity}} />
                    </MobileQty>
                  )}
                </Col>
                <Col xsHidden sm={3} md={2}>
                  <Body small>{!isEmpty(orderProducts) && quantity > 0 && `x ${quantity}`}</Body>
                </Col>
                <Col xsHidden sm={3} md={3}>
                  <Body small>{moment.unix(redemption.dateUnix).format("DD/MM/YY")}</Body>
                </Col>
              </Row>
            </Col>
            {!perkIsUnavailable && (
              <Col xs={5} sm={3}>
                {ViewPerkComponent}
                <StyledButton id="markAsUsedButtonId" onClick={onMarkAsUsed} small full>
                  <FormattedMessage
                    id={usedPerksOptionActive ? "perks.myPerks.myPerksItem.moveBack" : "perks.myPerks.myPerksItem.markAsUsed"} />
                </StyledButton>
              </Col>
            )}
          </StyledRow>
          {hasRedemptionExpired && <TimeLimitedDeal />}
          {perkIsUnavailable && (
            <Row>
              <Col xs={12}>
                <StyledHr />
              </Col>
              <Col xs={12}>
                <Row>
                  <StyledDeleteHeaderCol xs={8} sm={9}>
                    <Label large display="inline-block" marginBottom={1}>
                      <FormattedMessage id="perks.myPerks.myPerksItem.perkNotAvailable.header" />
                    </Label>
                    <Body small>
                      <FormattedMessage id="perks.myPerks.myPerksItem.perkNotAvailable.para" />
                    </Body>
                  </StyledDeleteHeaderCol>
                  <StyledDeleteButtonCol xs={4} sm={3}>
                    <StyledButton small onClick={this.handleArchiveRedemption}>
                      <FormattedMessage id="perks.myPerks.myPerksItem.perkNotAvailable.delete" />
                    </StyledButton>
                  </StyledDeleteButtonCol>
                </Row>
              </Col>
            </Row>
          )}
        </ItemContainer>
      </>
    )
  }
}

export default injectIntl(withBranding(MyPerkItem))
